import { FC, useState } from 'react';

import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';

interface RefundSectionProps {
  sortedCoupons: GiftCouponSummary[];
  onRefund: (couponId: string, amount: number, isAfterDelivery: boolean) => Promise<void>;
  isRefunding: boolean;
}

export const RefundSection: FC<RefundSectionProps> = ({
  sortedCoupons,
  onRefund,
  isRefunding,
}) => {
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const refundableCoupons = sortedCoupons.filter(
    (c) =>
      !c.refund &&
      c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
      c.redeemStatus !== GiftCouponRedeemStatus.Disposed &&
      c.redeemStatus !== GiftCouponRedeemStatus.Refunded,
  );

  const selectedCoupon = refundableCoupons.find((c) => c._id === selectedCouponId);
  const isAfterDelivery = selectedCoupon?.redeemStatus === GiftCouponRedeemStatus.Delivered;

  const handleSubmit = async () => {
    if (!selectedCouponId || !refundAmount) return;
    await onRefund(selectedCouponId, Number(refundAmount), isAfterDelivery);
    setSelectedCouponId('');
    setRefundAmount('');
    setShowConfirm(false);
  };

  if (refundableCoupons.length === 0) return null;

  return (
    <div className="mb-3 shrink-0 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-orange-800">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
        환불 처리
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium text-gray-600">대상 쿠폰</label>
          <select
            value={selectedCouponId}
            onChange={(e) => setSelectedCouponId(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-xs focus:border-orange-400 focus:outline-none"
          >
            <option value="">선택하세요</option>
            {refundableCoupons.map((c) => (
              <option key={c._id} value={c._id}>
                #{c.seq} ({c.code}) - {c.redeemStatus}
                {c.redeemStatus === GiftCouponRedeemStatus.Delivered ? ' [발송완료]' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium text-gray-600">환불 금액 (원)</label>
          <input
            type="number"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            placeholder="금액 입력"
            min={1}
            className="w-[120px] rounded border border-gray-300 px-2 py-1 text-xs focus:border-orange-400 focus:outline-none"
          />
        </div>
        <button
          onClick={() => setShowConfirm(true)}
          disabled={!selectedCouponId || !refundAmount || Number(refundAmount) <= 0}
          className="rounded bg-orange-600 px-3 py-1 text-xs font-bold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          환불
        </button>
      </div>
      {selectedCoupon && (
        <p className="mt-1 text-[10px] text-orange-700">
          {isAfterDelivery
            ? '발송 후 환불: redeemStatus는 Delivered 유지, refund 정보만 기록됩니다.'
            : '일반 환불: redeemStatus가 Refunded로 변경됩니다.'}
        </p>
      )}

      {showConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={() => setShowConfirm(false)} />
          <div className="relative rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-base font-bold text-gray-900">환불 확인</h3>
            <p className="mt-2 text-sm text-gray-600">
              쿠폰 #{selectedCoupon?.seq} ({selectedCoupon?.code})에 대해{' '}
              <span className="font-bold text-orange-600">
                {Number(refundAmount).toLocaleString()}원
              </span>
              을 환불하시겠습니까?
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {isAfterDelivery ? '(발송 후 환불)' : '(일반 환불)'}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={isRefunding}
                className="rounded bg-orange-600 px-4 py-2 text-sm font-bold text-white hover:bg-orange-700 disabled:opacity-50"
              >
                {isRefunding ? '처리중...' : '환불 실행'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
